/**
 * التفقيط: كتابة المبلغ بالحروف العربية.
 *
 * مطلوب على الفواتير الرسمية في مصر وغيرها، والغرض منه منع تعديل الرقم بعد
 * الإصدار: تغيير خانة في رقم سهل، وتغيير جملة مكتوبة ليس كذلك.
 *
 * تمييز العدد في العربية أربع حالات لا اثنتان، وتنظر إلى آخر خانتين لا إلى
 * العدد كله:
 *
 *   1          مفرد بعده "واحد"     جنيه واحد
 *   2          مثنى بلا عدد          جنيهان
 *   3 إلى 10   جمع مجرور             ثلاثة جنيهات
 *   11 إلى 99  مفرد منصوب            أحد عشر جنيهًا
 *   مضاعفات المئة والألف  مفرد مجرور  مائة جنيه، ألف جنيه
 *
 * وللمرتبة قاعدة إضافية: إذا تلاها المعدود صارت مضافة فتُجَرّ، فيقال
 * "أحد عشر ألف جنيه" لا "أحد عشر ألفًا جنيه".
 */

const ONES = [
  '',
  'واحد',
  'اثنان',
  'ثلاثة',
  'أربعة',
  'خمسة',
  'ستة',
  'سبعة',
  'ثمانية',
  'تسعة',
  'عشرة',
  'أحد عشر',
  'اثنا عشر',
  'ثلاثة عشر',
  'أربعة عشر',
  'خمسة عشر',
  'ستة عشر',
  'سبعة عشر',
  'ثمانية عشر',
  'تسعة عشر',
];

const TENS = ['', '', 'عشرون', 'ثلاثون', 'أربعون', 'خمسون', 'ستون', 'سبعون', 'ثمانون', 'تسعون'];

const HUNDREDS = [
  '',
  'مائة',
  'مائتان',
  'ثلاثمائة',
  'أربعمائة',
  'خمسمائة',
  'ستمائة',
  'سبعمائة',
  'ثمانمائة',
  'تسعمائة',
];

/** صيغ المعدود: المفرد والمثنى والجمع والمفرد المنصوب. */
export interface CountedForms {
  one: string;
  two: string;
  few: string;
  many: string;
}

const SCALES: CountedForms[] = [
  { one: '', two: '', few: '', many: '' },
  { one: 'ألف', two: 'ألفان', few: 'آلاف', many: 'ألفًا' },
  { one: 'مليون', two: 'مليونان', few: 'ملايين', many: 'مليونًا' },
  { one: 'مليار', two: 'ملياران', few: 'مليارات', many: 'مليارًا' },
];

/**
 * صيغة المثنى عند الإضافة.
 *
 * المثنى المضاف تُحذف نونه: مائتا جنيه لا مائتان جنيه، وألفا جنيه لا
 * ألفان جنيه. هذا أظهر ما يُلاحظ على مستند رسمي.
 */
const DUAL_CONSTRUCT: Readonly<Record<string, string>> = {
  مائتان: 'مائتا',
  ألفان: 'ألفا',
  مليونان: 'مليونا',
  ملياران: 'مليارا',
};

/**
 * يحوّل آخر كلمة مثنى إلى صيغة الإضافة إن وُجدت.
 *
 * الكلمة قد تسبقها واو العطف ملتصقة بها — "وألفان" — فتُفصل قبل المطابقة
 * وتُعاد بعدها، وإلا لم تُطابق أي مفتاح ومرّ الخطأ.
 */
function toConstruct(words: string): string {
  const parts = words.split(' ');
  const index = parts.length - 1;
  const last = parts[index]!;

  const prefix = last.startsWith('و') ? 'و' : '';
  const bare = prefix ? last.slice(1) : last;

  const constructed = DUAL_CONSTRUCT[bare];
  if (!constructed) return words;

  parts[index] = prefix + constructed;
  return parts.join(' ');
}

/** يحوّل عددًا من 1 إلى 999 إلى حروف. */
function underThousand(n: number): string {
  if (n === 0) return '';

  const parts: string[] = [];
  const hundreds = Math.floor(n / 100);
  const rest = n % 100;

  if (hundreds > 0) parts.push(HUNDREDS[hundreds]!);

  if (rest > 0) {
    if (rest < 20) {
      parts.push(ONES[rest]!);
    } else {
      const unit = rest % 10;
      const ten = Math.floor(rest / 10);
      // العربية تقدّم الآحاد على العشرات: خمسة وعشرون لا عشرون وخمسة.
      if (unit > 0) parts.push(`${ONES[unit]} و${TENS[ten]}`);
      else parts.push(TENS[ten]!);
    }
  }

  return parts.join(' و');
}

/**
 * يختار صيغة المعدود.
 *
 * @param construct  هل المعدود مضاف إلى ما بعده؟ المضاف يُجَرّ فلا يُنصب،
 *                   ولذلك تصير "أحد عشر ألف جنيه" لا "أحد عشر ألفًا جنيه".
 */
function pickForm(count: number, forms: CountedForms, construct = false): string {
  const lastTwo = count % 100;

  if (count === 1 || lastTwo === 1 || lastTwo === 0) return forms.one;
  if (count === 2 || lastTwo === 2) return forms.two;
  if (lastTwo >= 3 && lastTwo <= 10) return forms.few;
  return construct ? forms.one : forms.many;
}

/**
 * يضم العدد إلى معدوده.
 *
 * الواحد والاثنان يُدمجان في المعدود ولا يُذكران عددًا: مائة وجنيهان لا
 * مائة واثنان جنيهان. وما عداهما يسبق العدد المعدود.
 */
function joinCount(
  count: number,
  forms: CountedForms,
  options: { construct?: boolean; withWahid?: boolean } = {},
): string {
  const { construct = false, withWahid = false } = options;
  const lastTwo = count % 100;

  const single = withWahid ? `${forms.one} واحد` : forms.one;

  if (count === 1) return single;
  if (count === 2) return forms.two;

  if (lastTwo === 1) {
    const head = integerToArabicWords(count - 1);
    return head ? `${head} و${single}` : single;
  }
  if (lastTwo === 2) {
    const head = integerToArabicWords(count - 2);
    return head ? `${head} و${forms.two}` : forms.two;
  }

  return `${toConstruct(integerToArabicWords(count))} ${pickForm(count, forms, construct)}`;
}

/**
 * يحوّل عددًا صحيحًا إلى حروف عربية.
 *
 * المرتبة الأخيرة تُعامل كمضاف دائمًا لأن المعدود يتلوها في الاستخدام
 * الغالب. فإن استُعمل العدد وحده فالفرق طفيف ولا يُخطئ القارئ فهمه.
 */
export function integerToArabicWords(value: number): string {
  const n = Math.floor(Math.abs(value));
  if (n === 0) return 'صفر';

  const groups: number[] = [];
  let rest = n;
  while (rest > 0) {
    groups.push(rest % 1000);
    rest = Math.floor(rest / 1000);
  }

  const parts: string[] = [];
  for (let i = groups.length - 1; i >= 0; i--) {
    const group = groups[i]!;
    if (group === 0) continue;

    if (i === 0) {
      parts.push(underThousand(group));
      continue;
    }

    const scale = SCALES[i] ?? SCALES[0]!;
    const lastTwo = group % 100;

    /*
     * المرتبة مضافة إلى المعدود فقط إذا لم يتبعها جزء عددي آخر.
     *
     * في "مائة وأربعة عشر ألف جنيه" تلي الألفَ العملةُ مباشرة فتُجَرّ.
     * أما في "واحد وخمسون ألفًا وثلاثمائة جنيه" فيفصل بينهما عطف، فتعود
     * الألف تمييزًا منصوبًا.
     */
    const followedByMore = groups.slice(0, i).some((g) => g !== 0);

    if (group === 1) {
      parts.push(scale.one);
    } else if (group === 2) {
      parts.push(scale.two);
    } else if (lastTwo === 1) {
      parts.push(`${underThousand(group - 1)} و${scale.one}`);
    } else if (lastTwo === 2) {
      parts.push(`${underThousand(group - 2)} و${scale.two}`);
    } else {
      parts.push(`${underThousand(group)} ${pickForm(group, scale, !followedByMore)}`);
    }
  }

  return parts.join(' و');
}

export interface CurrencyWords {
  majorForms: CountedForms;
  minorForms: CountedForms;
}

export const EGP_WORDS: CurrencyWords = {
  majorForms: { one: 'جنيه', two: 'جنيهان', few: 'جنيهات', many: 'جنيهًا' },
  minorForms: { one: 'قرش', two: 'قرشان', few: 'قروش', many: 'قرشًا' },
};

export const SAR_WORDS: CurrencyWords = {
  majorForms: { one: 'ريال', two: 'ريالان', few: 'ريالات', many: 'ريالًا' },
  minorForms: { one: 'هللة', two: 'هللتان', few: 'هللات', many: 'هللة' },
};

export const AED_WORDS: CurrencyWords = {
  majorForms: { one: 'درهم', two: 'درهمان', few: 'دراهم', many: 'درهمًا' },
  minorForms: { one: 'فلس', two: 'فلسان', few: 'فلوس', many: 'فلسًا' },
};

export const USD_WORDS: CurrencyWords = {
  majorForms: { one: 'دولار', two: 'دولاران', few: 'دولارات', many: 'دولارًا' },
  minorForms: { one: 'سنت', two: 'سنتان', few: 'سنتات', many: 'سنتًا' },
};

export const CURRENCY_WORDS: Readonly<Record<string, CurrencyWords>> = {
  EGP: EGP_WORDS,
  SAR: SAR_WORDS,
  AED: AED_WORDS,
  USD: USD_WORDS,
};

/**
 * تفقيط مبلغ مالي.
 *
 * @example  tafqit(1250.75)  →  فقط ألف ومائتان وخمسون جنيهًا وخمسة وسبعون قرشًا لا غير
 * @example  tafqit(11000)    →  فقط أحد عشر ألف جنيه لا غير
 * @example  tafqit(102)      →  فقط مائة وجنيهان لا غير
 */
export function tafqit(amount: number, currency: CurrencyWords | string = EGP_WORDS): string {
  const words = typeof currency === 'string' ? (CURRENCY_WORDS[currency] ?? EGP_WORDS) : currency;

  const negative = amount < 0;
  const abs = Math.abs(amount);

  const major = Math.floor(abs);
  // التقريب على الكسور قبل الفصل، وإلا ظهر 0.999 صفرًا.
  const minor = Math.round((abs - major) * 100);

  // التقريب قد يدفع الكسور إلى مئة كاملة، فتُضاف إلى الصحيح.
  const majorFinal = minor === 100 ? major + 1 : major;
  const minorFinal = minor === 100 ? 0 : minor;

  const parts: string[] = [];

  if (majorFinal > 0) {
    parts.push(joinCount(majorFinal, words.majorForms, { withWahid: true }));
  } else if (minorFinal === 0) {
    parts.push(`صفر ${words.majorForms.one}`);
  }

  if (minorFinal > 0) {
    parts.push(joinCount(minorFinal, words.minorForms, { withWahid: true }));
  }

  return `${negative ? 'سالب ' : ''}فقط ${parts.join(' و')} لا غير`;
}
